import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { selectedQuantity, updateQuantity, productData } from "../store/product-store";
import { getLimitStatus } from "../store/cart-store";

@customElement("quantity-selector-pp")
export class QuantitySelectorPP extends LitElement {
  @state() private _quantity = 1;
  @state() private _atLimit = false;
  // Max units that can still be added this order (limit − units already in cart).
  // Infinity when no limit applies.
  private _maxAddable = Infinity;

  constructor() {
    super();
    effect(() => {
      this._quantity = selectedQuantity.value;
    });
    effect(() => {
      const qty = selectedQuantity.value;
      const pd = productData.value;
      if (!pd?.id) {
        this._maxAddable = Infinity;
        this._atLimit = false;
        return;
      }
      const status = getLimitStatus(pd.id, pd.vendor ?? "");
      this._maxAddable =
        status.type !== "none" ? Math.max(status.remaining, 0) : Infinity;
      this._atLimit = qty >= this._maxAddable;

      // Clamp the current selection if the cart changed and it now exceeds
      // what can still be added (keep at least 1 so the input stays usable).
      if (this._maxAddable >= 1 && qty > this._maxAddable) {
        updateQuantity(this._maxAddable);
      }
    });
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  private _decrement() {
    if (this._quantity > 1) {
      updateQuantity(this._quantity - 1);
    }
  }

  private _increment() {
    if (this._atLimit) return;
    updateQuantity(this._quantity + 1);
  }

  private _handleInput(e: Event) {
    const val = parseInt((e.target as HTMLInputElement).value, 10);
    if (!isNaN(val) && val >= 1) {
      // Never let a typed value exceed what can still be added this order.
      updateQuantity(Math.min(val, this._maxAddable));
    }
  }

  render() {
    const buttonClass = "shrink-0 w-[25px] h-[25px] flex items-center justify-center rounded-[9999px] border border-[var(--color-neutral-500)] bg-transparent p-0 cursor-pointer [&_svg]:w-3 [&_svg]:h-3 [&_svg]:pointer-events-none";
    return html`
    <div class="quantity relative flex items-center gap-2">
      <button
        class="quantity__button no-js-hidden ${buttonClass}${this._quantity <= 1 ? " opacity-50 cursor-not-allowed pointer-events-none" : ""}"
        name="minus"
        type="button"
        tabindex="0"
        ?disabled=${this._quantity <= 1}
        @click=${this._decrement}
      >
        <span class="sr-only">Decrease quantity</span>
        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" viewBox="0 0 10 2">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"/>
        </svg>
      </button>
      <input
        class="quantity__input flex-1 min-w-0 text-center appearance-none outline-none focus:outline-none focus:ring-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [-moz-appearance:textfield]"
        type="number"
        name="quantity"
        min="1"
        .value=${String(this._quantity)}
        @change=${this._handleInput}
      />
      <button
        class="quantity__button no-js-hidden ${buttonClass}${this._atLimit ? " opacity-50 cursor-not-allowed pointer-events-none" : ""}"
        name="plus"
        type="button"
        tabindex="0"
        ?disabled=${this._atLimit}
        @click=${this._increment}
      >
        <span class="sr-only">Increase quantity</span>
        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" viewBox="0 0 10 10">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"/>
        </svg>
      </button>
      </div>
    `;
  }
}
